/* Premium Navigation Labels for Dawn v15
   Supports badge style, theme, exact X/Y position and rotation.
   No jQuery, no external libraries, no DOM duplication. */

(() => {
  const ROOT_SELECTOR = '.header__inline-menu, .menu-drawer__navigation';
  const TARGET_SELECTOR = [
    '.header__menu-item',
    '.mega-menu__link',
    '.menu-drawer__menu-item'
  ].join(', ');

  const DYNAMIC_CLASS_PREFIXES = [
    'nav-label-position-',
    'nav-label-style-',
    'nav-label-theme-',
    'nav-label-animation-'
  ];

  const DYNAMIC_PROPERTIES = [
    '--nav-label-color',
    '--nav-label-hover-color',
    '--nav-label-active-color',
    '--nav-label-bg',
    '--nav-label-text',
    '--nav-label-radius',
    '--nav-label-size',
    '--nav-label-weight',
    '--nav-label-spacing',
    '--nav-label-pad-y',
    '--nav-label-pad-x',
    '--nav-label-size-tablet',
    '--nav-label-size-mobile',
    '--nav-label-pad-y-tablet',
    '--nav-label-pad-x-tablet',
    '--nav-label-pad-y-mobile',
    '--nav-label-pad-x-mobile',
    '--nav-label-animation-speed',
    '--nav-label-position-x',
    '--nav-label-position-y',
    '--nav-label-degree'
  ];

  const normalize = (value) => (value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  function cleanText(element) {
    const clone = element.cloneNode(true);
    clone.querySelectorAll('.nav-label-badge, svg, .svg-wrapper').forEach((node) => node.remove());
    return normalize(clone.textContent);
  }

  function removeExistingLabels() {
    document.querySelectorAll('.nav-label-target').forEach((element) => {
      [...element.classList].forEach((className) => {
        if (DYNAMIC_CLASS_PREFIXES.some((prefix) => className.startsWith(prefix))) {
          element.classList.remove(className);
        }
      });

      element.classList.remove('nav-label-target', 'nav-label-active');
      DYNAMIC_PROPERTIES.forEach((property) => element.style.removeProperty(property));
      delete element.dataset.navigationLabelTarget;
    });

    document.querySelectorAll('.nav-label-badge').forEach((badge) => badge.remove());
  }

  function setCSSProperties(element, config) {
    const values = {
      '--nav-label-bg': config.badge_background,
      '--nav-label-text': config.badge_text_color,
      '--nav-label-radius': `${config.border_radius ?? 4}px`,
      '--nav-label-size': `${config.font_size_desktop ?? 9}px`,
      '--nav-label-weight': config.font_weight || '600',
      '--nav-label-spacing': `${config.letter_spacing ?? 1}px`,
      '--nav-label-pad-y': `${config.padding_vertical ?? 3}px`,
      '--nav-label-pad-x': `${config.padding_horizontal ?? 6}px`,
      '--nav-label-size-tablet': `${config.font_size_tablet ?? 9}px`,
      '--nav-label-size-mobile': `${config.font_size_mobile ?? 8}px`,
      '--nav-label-pad-y-tablet': `${config.padding_vertical_tablet ?? 3}px`,
      '--nav-label-pad-x-tablet': `${config.padding_horizontal_tablet ?? 6}px`,
      '--nav-label-pad-y-mobile': `${config.padding_vertical_mobile ?? 3}px`,
      '--nav-label-pad-x-mobile': `${config.padding_horizontal_mobile ?? 6}px`,
      '--nav-label-animation-speed': `${config.animation_speed ?? 2.5}s`,
      '--nav-label-position-x': `${config.badge_position_x ?? 0}px`,
      '--nav-label-position-y': `${config.badge_position_y ?? 0}px`,
      '--nav-label-degree': `${config.badge_degree ?? 0}deg`
    };

    Object.entries(values).forEach(([property, value]) => {
      element.style.setProperty(property, value);
    });

    if (config.change_color === true) {
      element.style.setProperty('--nav-label-color', config.normal_color || 'inherit');
      element.style.setProperty('--nav-label-hover-color', config.hover_color || config.normal_color || 'inherit');
      element.style.setProperty('--nav-label-active-color', config.active_color || config.hover_color || config.normal_color || 'inherit');
    }
  }

  function applyLabel(element, config) {
    const showBadge = config.show_badge === true;
    const changeColor = config.change_color === true;
    if (!showBadge && !changeColor) return;

    const badgePosition = config.badge_position || config.position || 'right';
    const badgeStyle = config.badge_style || 'filled';
    const badgeTheme = config.badge_theme || 'classic';

    element.classList.add('nav-label-target');
    element.dataset.navigationLabelTarget = config.target_menu_item || '';

    if (element.getAttribute('aria-current') === 'page') {
      element.classList.add('nav-label-active');
    }

    setCSSProperties(element, config);

    if (!showBadge || !config.label_text) return;

    const badge = document.createElement('span');
    badge.className = 'nav-label-badge';
    badge.textContent = config.label_text;
    badge.style.textTransform = config.text_transform || 'uppercase';
    badge.setAttribute('aria-hidden', 'true');

    element.classList.add(`nav-label-position-${badgePosition}`);
    element.classList.add(`nav-label-style-${badgeStyle}`);
    element.classList.add(`nav-label-theme-${badgeTheme}`);

    if (config.animation && config.animation !== 'none') {
      element.classList.add(`nav-label-animation-${config.animation}`);
    }

    element.appendChild(badge);
  }

  function findMatches(config) {
    const wantedText = normalize(config.target_menu_item);
    const wantedUrl = normalize(config.target_url);
    if (!wantedText && !wantedUrl) return [];

    const matches = [];
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      root.querySelectorAll(TARGET_SELECTOR).forEach((element) => {
        const textMatch = !wantedText || cleanText(element) === wantedText;
        const url = normalize(element.getAttribute('href'));
        const urlMatch = !wantedUrl || url === wantedUrl;
        if (textMatch && urlMatch) matches.push(element);
      });
    });
    return matches;
  }

  function init() {
    const configElement = document.getElementById('NavigationLabelsConfig');
    if (!configElement) return;

    let configs;
    try {
      configs = JSON.parse(configElement.textContent || '[]');
    } catch (error) {
      return;
    }

    removeExistingLabels();

    configs
      .filter((config) => config.enable === true)
      .sort((a, b) => Number(a.priority || 50) - Number(b.priority || 50))
      .forEach((config) => {
        findMatches(config).forEach((element) => applyLabel(element, config));
      });
  }

  document.addEventListener('DOMContentLoaded', init);
  window.addEventListener('load', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);
  document.addEventListener('shopify:block:select', init);
})();
